import React, { useEffect, useRef, useState } from 'react';
import { Volume2, VolumeX, Play, Pause } from 'lucide-react';
import { Button } from './ui/button';
import { fetchActiveMusic, resolveImageUrl } from '../services/api';

const FALLBACK_TRACK = {
  title: 'Mystic Sounds',
  url: 'https://assets.mixkit.co/music/preview/mixkit-dreaming-big-31.mp3',
};

const MusicPlayer = () => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [track, setTrack] = useState(FALLBACK_TRACK);

  useEffect(() => {
    let cancelled = false;
    fetchActiveMusic()
      .then((data) => {
        if (cancelled) return;
        if (data && data.url) {
          setTrack({
            title: data.title || 'Mystic Sounds',
            url: resolveImageUrl(data.url),
          });
        }
      })
      .catch(() => { /* fallback already in state */ });
    return () => { cancelled = true; };
  }, []);

  // Whenever track changes, attempt to play at low volume
  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    el.volume = 0.15;
    el.load();
    const playPromise = el.play();
    if (playPromise !== undefined) {
      playPromise
        .then(() => setIsPlaying(true))
        .catch(() => setIsPlaying(false));
    }
    return () => {
      el.pause();
    };
  }, [track]);

  const togglePlay = () => {
    const el = audioRef.current;
    if (!el) return;
    if (isPlaying) {
      el.pause();
      setIsPlaying(false);
    } else {
      el.play().then(() => setIsPlaying(true)).catch(() => setIsPlaying(false));
    }
  };

  const toggleMute = () => {
    const el = audioRef.current;
    if (!el) return;
    el.muted = !isMuted;
    setIsMuted(!isMuted);
  };

  return (
    <div className="fixed bottom-6 right-6 z-50" data-testid="music-player">
      <div className="flex items-center gap-2 bg-emerald-900/90 backdrop-blur-md rounded-full px-4 py-3 shadow-2xl border border-amber-500/40 mystical-glow-gold">
        <span
          className="text-sm text-amber-100 font-medium tracking-wide max-w-[160px] truncate"
          style={{ fontFamily: 'Cinzel, serif' }}
          title={track.title}
        >
          {track.title}
        </span>
        <Button
          size="sm"
          variant="ghost"
          onClick={togglePlay}
          className="h-8 w-8 rounded-full p-0 hover:bg-emerald-800"
          data-testid="music-toggle-play"
        >
          {isPlaying ? (
            <Pause className="h-4 w-4 text-amber-400" />
          ) : (
            <Play className="h-4 w-4 text-amber-400" />
          )}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={toggleMute}
          className="h-8 w-8 rounded-full p-0 hover:bg-emerald-800"
          data-testid="music-toggle-mute"
        >
          {isMuted ? (
            <VolumeX className="h-4 w-4 text-amber-400" />
          ) : (
            <Volume2 className="h-4 w-4 text-amber-400" />
          )}
        </Button>
      </div>

      <audio ref={audioRef} loop preload="auto">
        <source src={track.url} />
      </audio>
    </div>
  );
};

export default MusicPlayer;
